// src/Services/pdf/adapters/reportToDoc.js
// Adapter: convert a simple columns/rows report into a JSON DocumentModel for PdfService

import { buildGridFromRows } from '@/Services/pdf/blocks/grid';

/**
 * @typedef {{ label: string, field: string, width?: number|string }} Column
 * @typedef {{ title: string, columns: Column[], rows: Array<Record<string, any>>, options?: { subtitle?: string } }} ReportInput
 */

// A4 page geometry in pdfmake points. Landscape is the same sheet rotated, so
// its usable width is the longer edge.
const A4_PORTRAIT_WIDTH_PT = 595.28;
const A4_LANDSCAPE_WIDTH_PT = 841.89;
// This adapter always applies [24, 24, 24, 24] page margins (see the returned
// model.page), so the left+right margins consume 48pt of every page.
const PAGE_HORIZONTAL_MARGINS_PT = 48;
// Reports with more than this many columns flip to landscape, matching the
// threshold the legacy pickOrientation helper used before the JSON pipeline.
const LANDSCAPE_COLUMN_THRESHOLD = 6;
// Body font size applied by this adapter and assumed by the width measurement
// below — pdfmake glyph widths scale with font size, so the two must not drift.
const REPORT_TABLE_FONT_SIZE = 10;
// Roboto digits and uppercase letters average ≈0.6 × fontSize in width; the
// extra 8% absorbs drift between this estimate and pdfmake's real measurement.
const GLYPH_WIDTH_FACTOR = 0.6;
const WIDTH_SAFETY_FACTOR = 1.08;
// 6pt left + 6pt right cell padding from buildGridFromRows' layout. pdfmake
// adds this padding OUTSIDE the specified column width, so widths passed to
// pdfmake are content-only and the padding must be reserved from the page.
const CELL_HORIZONTAL_PADDING_PT = 12;

function longestTokenLength(value) {
  return String(value == null ? '' : value)
    .split(/\s+/)
    .reduce((max, token) => Math.max(max, token.length), 0);
}

/**
 * Decide page orientation and pdfmake column widths for a report table.
 *
 * pdfmake treats an unbroken string as a single unbreakable unit: when a word
 * is wider than its column the extra characters are silently dropped instead
 * of wrapped, which is how ticket #28759 lost everything after "INV00100" in
 * the Smart Invoice numbers. Each column is therefore measured against its
 * longest single token (header or cell) so no cell can ever lose characters,
 * while multi-word cells still wrap normally at spaces.
 *
 * @param {string[]} headerLabels
 * @param {Array<Array<{text: string}|string>>} bodyRows cell objects as built by reportToDoc
 * @param {number} fontSize effective body font size used for glyph estimation
 * @returns {{ orientation: string, widths: Array<number>|null }} widths === null keeps pdfmake's content-driven 'auto' sizing, which is safe because the content already fits the page
 */
function resolveTableLayout(headerLabels, bodyRows, fontSize) {
  // Widths passed to pdfmake exclude cell padding (pdfmake draws padding on
  // top of the given width), so the page must fit content plus padding.
  const tokenWidths = headerLabels.map((label, colIdx) => {
    let longest = longestTokenLength(label);
    for (const row of bodyRows) {
      const cell = row[colIdx];
      const text = cell && typeof cell === 'object' ? cell.text : cell;
      longest = Math.max(longest, longestTokenLength(text));
    }
    return longest * GLYPH_WIDTH_FACTOR * fontSize * WIDTH_SAFETY_FACTOR;
  });
  const columnCount = headerLabels.length;
  const naturalTotal = tokenWidths.reduce((sum, width) => sum + width, 0);
  const portraitUsable = A4_PORTRAIT_WIDTH_PT - PAGE_HORIZONTAL_MARGINS_PT - columnCount * CELL_HORIZONTAL_PADDING_PT;
  if (naturalTotal <= portraitUsable) {
    // Content fits portrait as-is: keep today's 'auto' sizing untouched so
    // small reports render exactly as they always have.
    return { orientation: 'portrait', widths: null };
  }
  const preferLandscape = columnCount > LANDSCAPE_COLUMN_THRESHOLD;
  const orientation = preferLandscape ? 'landscape' : 'portrait';
  const usable = (preferLandscape ? A4_LANDSCAPE_WIDTH_PT : A4_PORTRAIT_WIDTH_PT)
    - PAGE_HORIZONTAL_MARGINS_PT - columnCount * CELL_HORIZONTAL_PADDING_PT;
  if (naturalTotal <= usable) {
    // Measured widths guarantee pdfmake can render the longest token of every
    // column without dropping characters.
    return { orientation, widths: tokenWidths };
  }
  // Content cannot fit either orientation. Proportional scaling keeps the
  // columns in their natural size relationship instead of letting one wide
  // column starve the rest.
  return { orientation, widths: tokenWidths.map((width) => (width / naturalTotal) * usable) };
}

/**
 * Map a generic tabular report to DocumentModel.
 * - Applies basic alignment for numeric-like fields.
 * - Leaves money formatting to caller where possible (or downstream tokens), but aligns right.
 * - Picks orientation and column widths so no cell value is cut off (ticket #28759).
 *
 * @param {ReportInput} input
 */
export function reportToDoc(input) {
  try { if (typeof window !== 'undefined' && window.PDF_DEBUG !== false) console.log('[PDF][adapter][reportToDoc] start', { title: input?.title, columns: input?.columns?.length, rows: input?.rows?.length }); } catch(_){}
  const title = input.title || 'Report';
  // Normalize columns to ensure a usable field key
  const cols = (input.columns || []).map(c => ({
    label: c.label || c.key || c.field || '',
    field: c.field || c.key || c.label || '',
    width: c.width || 'auto'
  }));
  const headers = cols.map(c => c.label);

  const srcRows = Array.isArray(input.rows) ? input.rows : [];
  const rows = srcRows.map(r => {
    // Support both object rows and array rows
    if (Array.isArray(r)) {
      return cols.map((c, idx) => {
        const v = r[idx];
        const isNum = /count|total|rate|amount|value|qty|percent|conversion|revenue|sales|price|cost/i.test((c.field || '').toLowerCase());
        return isNum ? { text: v != null ? String(v) : '', alignment: 'right' } : { text: v != null ? String(v) : '' };
      });
    }
    return cols.map(c => {
      const key = (c.field || '').toLowerCase();
      const v = r[c.field];
      const isNum = /count|total|rate|amount|value|qty|percent|conversion|revenue|sales|price|cost/i.test(key);
      return isNum ? { text: v != null ? String(v) : '', alignment: 'right' } : { text: v != null ? String(v) : '' };
    });
  });

  const content = [];
  if (input.options && input.options.subtitle) {
    content.push({ type: 'text', text: input.options.subtitle, style: { fontSize: 10, color: '#666' }, margin: [0, 0, 0, 8] });
  }
  content.push({ type: 'text', text: title, style: 'h1', margin: [0, 0, 0, 8] });

  // Optional totals row: when requested, auto-sum numeric-like columns (amount/value/total/sales/revenue/vat).
  // Computed BEFORE the table node so the totals row participates in the
  // column-width measurement below — a column's sum is usually wider than any
  // single cell and would otherwise be the next value to get cut off.
  if (input.options && input.options.showTotals) {
    const sumableIdx = cols
      .map((c, idx) => (/total|amount|value|sales|revenue|vat/i.test(((c.field||'') + ' ' + (c.label||'')).toLowerCase()) ? idx : -1))
      .filter(i => i >= 0);

    if (sumableIdx.length) {
      const totalsRow = new Array(cols.length).fill(null).map(() => ({ text: '' }));
      // Label cell: "Totals"
      if (totalsRow.length) totalsRow[0] = { text: 'Totals', style: 'th' };
      sumableIdx.forEach(idx => {
        // Sum by parsing numeric parts from each cell in that column
        let sum = 0;
        for (const row of rows) {
          const cell = row[idx];
          const raw = (cell && (cell.text != null ? cell.text : cell)) ?? '';
          const n = Number(String(raw).toString().replace(/[^0-9.-]/g, ''));
          if (isFinite(n)) sum += n;
        }
        totalsRow[idx] = { text: (typeof window !== 'undefined' && window.$utils && window.$utils.formatMoney) ? window.$utils.formatMoney(sum) : String(sum), alignment: 'right', bold: true };
      });
      rows.push(totalsRow);
    }
  }

  // Callers that pass explicit per-column widths keep full control of layout;
  // everyone else gets measured widths so no column can drop characters.
  // Inspect the RAW caller columns here — the normalized cols above get a
  // default width:'auto' stamped onto every entry, which would otherwise make
  // every report look like it asked for manual widths.
  const hasExplicitWidths = (input.columns || []).some((c) => c && c.width);
  const layout = hasExplicitWidths ? null : resolveTableLayout(headers, rows, REPORT_TABLE_FONT_SIZE);
  const widths = hasExplicitWidths ? cols.map((c) => c.width || 'auto') : (layout && layout.widths);

  // Emit a native pdfmake table node via helper to avoid relying on the 'grid' alias
  const tableNode = buildGridFromRows({ headers, rows, widths });

  content.push(tableNode);

  return {
    meta: { title },
    page: { size: 'A4', margin: [24, 24, 24, 24], orientation: (layout && layout.orientation) || 'portrait' },
    styles: {
      h1: { fontSize: 16, bold: true },
      th: { bold: true },
    },
    // Kept in sync with resolveTableLayout: the measured column widths assume
    // this exact body font size.
    defaultStyle: { fontSize: REPORT_TABLE_FONT_SIZE },
    content
  };
}
// Log completion
try { if (typeof window !== 'undefined' && window.PDF_DEBUG !== false) console.log('[PDF][adapter][reportToDoc] ready'); } catch(_){}
